import { del, get, patch, post } from './apiService';
import type {
  Connection,
  ConnectionSegment,
  CreateConnectionRequest,
  ListConnectionsResponse,
  UserSettableConnectionType,
} from '../types/connection';

/**
 * Sends a connection request. Returns the created (or, if a live connection
 * already exists, the existing) `Connection` row — the backend dedupes in
 * either direction, so this is idempotent against re-requests.
 */
export async function createConnection(
  body: CreateConnectionRequest,
): Promise<Connection> {
  return post<Connection>('/connections', body);
}

/**
 * Resolves a `/c={id}` deep link for the signed-in caller. Server-side this
 * has side effects: it stamps the caller as recipient on a shell row, clones
 * the row for a forwarded link that was already accepted/rejected, or returns
 * the row untouched when still pending / the caller is the initiator. Because
 * the forwarded-link case clones on every call, this MUST be invoked exactly
 * once per landing (the hook enforces that). Returns the resolved row with the
 * initiator's `profile` preloaded.
 */
export async function resolveConnection(id: string): Promise<Connection> {
  return get<Connection>(`/connections/resolve/${id}`);
}

/**
 * Accepts a pending connection request. Recipient-only (enforced server-side).
 * Returns the updated `Connection` row (now `acceptedAt`-stamped). Idempotent
 * — re-accepting an already-accepted row is a no-op return.
 */
export async function acceptConnection(id: string): Promise<Connection> {
  return post<Connection>(`/connections/${id}/accept`, {});
}

/**
 * Declines a pending connection request. Recipient-only (enforced
 * server-side). Returns the updated `Connection` row (now `rejectedAt`-
 * stamped). Cannot reject an already-accepted row (use disconnect instead).
 */
export async function rejectConnection(id: string): Promise<Connection> {
  return post<Connection>(`/connections/${id}/reject`, {});
}

/**
 * Re-categorizes a connection's type. Initiator-only (enforced server-side),
 * and only to a user-settable type (friend / family / connection) — share
 * types are write-once. Returns the updated `Connection` row.
 */
export async function patchConnectionType(
  id: string,
  type: UserSettableConnectionType,
): Promise<Connection> {
  return patch<Connection>(`/connections/${id}`, { type });
}

/**
 * Disconnects / removes a connection. Either party may delete; it's a soft
 * delete server-side with no notification to the other party. Also serves as
 * "withdraw" for a pending request the caller sent. Returns nothing (204).
 */
export async function deleteConnection(id: string): Promise<void> {
  return del<void>(`/connections/${id}`);
}

/**
 * Leaves the marketing-QR campaign that auto-created the given connection.
 *
 * Two effects on the server:
 *  - Always: flags every marketing_data row the caller has for this
 *    connection's campaign as `disable_group_connections=true`, so future
 *    fan-outs skip them and newcomers don't reconnect to them.
 *  - When `removeConnections=true`: soft-deletes every connection row for
 *    the caller's profile whose `qr_code_id` matches this connection's,
 *    scoped by qr_code_id so other campaigns' connections stay intact.
 *
 * Rejected server-side when the connection isn't tied to a campaign
 * (qr_code_id null) or the caller isn't a party. Returns nothing (204).
 */
export async function leaveGroup(
  id: string,
  removeConnections: boolean,
): Promise<void> {
  return post<void>(`/connections/${id}/leave-group`, { removeConnections });
}

/**
 * Lists the caller's connections for a segment (default `all`):
 *   - accepted          — live connections in either role
 *   - requests_to_me     — pending requests waiting on the caller
 *   - requests_from_me   — pending requests the caller sent
 *   - all                — every non-deleted row the caller is in
 *
 * `expand=qrCode` is passed on the `accepted` segment so the row-render can
 * show the campaign group label without a follow-up fetch. Other segments
 * (pending inbox / outbox) never render that label, so the expand isn't
 * needed — leaving it off keeps those payloads smaller.
 */
export async function getMyConnections(
  segment: ConnectionSegment = 'all',
): Promise<Connection[]> {
  const params = new URLSearchParams({ segment });
  if (segment === 'accepted') params.set('expand', 'qrCode');
  const res = await get<ListConnectionsResponse>(
    `/connections?${params.toString()}`,
  );
  return res.connections;
}
